import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight, MapPin, Mail, Globe, Trophy, Music, Users, Calendar,
  GraduationCap, Briefcase, Heart, PartyPopper, Star, Sparkles, Zap,
  BookOpen, Check, Building2, MessageCircle,
} from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import { MILESTONES, VALUES, BRANCHES } from "@/lib/data";

const BRANCH_STORIES = {
  india: {
    flag: "🇮🇳",
    label: "India",
    city: "Chennai",
    heading: (
      <>
        Started with 3 students.<br />
        <span className="phoenix-text">Now serving thousands.</span>
      </>
    ),
    paragraphs: [
      "Phoenix Dance Academy was founded in 2014 by Manikandan Gopinath in Chennai, India — with a vision to redefine dance training and bring world-class learning experiences to aspiring dancers.",
      "From a humble studio with just 3 students in Chennai has now grown into a global dance family, expanding across India, the USA, and Canada.",
      "PDA USA was launched in Sacramento in 2021 by Gowri Sathish, followed by PDA Canada in Kitchener in 2022 by Sandhya Vijayakumar.",
      "Today, Phoenix Dance Academy has proudly trained and inspired over 10,000 students worldwide — nurturing talent, confidence, creativity, and passion through dance.",
    ],
  },
  usa: {
    flag: "🇺🇸",
    label: "USA",
    city: "Sacramento",
    heading: (
      <>
        Dance is more than <span className="phoenix-text">movement.</span><br />
        It's how we live.
      </>
    ),
    paragraphs: [
      "At Phoenix Dance Academy USA, we believe dance is confidence, creativity, discipline, culture, fitness, and self-expression — all woven into one art form.",
      "Founded and led by Gowri Sathish along with renowned choreographer Manikandan Gopinath, PDA USA offers high-energy, professional Indo-Western dance training for students of all ages and levels.",
      "With branches and collaborations across India, USA, and Canada, PDA USA has trained hundreds of students through both online and in-person programs — creating award-winning performances and unforgettable stage experiences.",
    ],
  },
  canada: {
    flag: "🇨🇦",
    label: "Canada",
    city: "Kitchener",
    heading: (
      <>
        Dance is <span className="phoenix-text">for everyone.</span><br />
        Hello, Kitchener.
      </>
    ),
    paragraphs: [
      "Phoenix Dance Academy Canada was launched in 2025 by Sandhya Vijayakumar in Kitchener, Ontario — born from years of online mentorship under PDA Founder Manikandan Gopinath and a deep belief that dance belongs to every age group, every body, every story.",
      "By profession a Software QA Analyst, Sandhya brings structure, precision, and discipline to the studio — while specialising in Indian Cinematic, Indian Folk, Contemporary, and Hip-Hop for Kids, Teens, and Adults.",
      "What started with just 3 kindergarten kids has grown to 25+ active students within one year — choreographing for weddings, live music events, baby showers, and major cultural festivals across Ontario's South-Asian community.",
    ],
  },
};

export default function About() {
  const [branch, setBranch] = useState("india");
  const story = BRANCH_STORIES[branch];
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[480px] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/1701202/pexels-photo-1701202.jpeg?auto=compress&cs=tinysrgb&w=2000"
          alt=""
          loading="eager"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ About Phoenix</p>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-[8rem] leading-[0.9] text-white">
            Born from <span className="phoenix-text">fire.</span><br />
            Built for stages.
          </h1>
        </div>
      </section>

      {/* Story — branch selector + dynamic content */}
      <section className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Branch selector */}
        <div data-testid="about-branch-selector" className="mb-14 lg:mb-20">
          <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Choose a branch</p>
          <h3 className="font-display text-2xl lg:text-3xl text-white mb-6">
            Which PDA story do you want to read?
          </h3>
          <div className="inline-flex flex-wrap gap-2 p-1.5 border border-white/10 bg-[#0a0a0a]">
            {Object.entries(BRANCH_STORIES).map(([id, b]) => (
              <button
                key={id}
                data-testid={`about-branch-${id}`}
                onClick={() => setBranch(id)}
                className={`px-5 lg:px-7 h-11 text-xs uppercase tracking-[0.2em] font-semibold transition-all inline-flex items-center gap-2 ${
                  branch === id
                    ? "bg-[#ff4500] text-white shadow-[0_0_18px_rgba(255,69,0,0.45)]"
                    : "text-white/60 hover:text-white"
                }`}
              >
                <span className="text-base leading-none">{b.flag}</span>
                <span>{b.label}</span>
                {branch === id && <span className="text-[#ffb700] hidden sm:inline">·</span>}
                {branch === id && <span className="hidden sm:inline">{b.city}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Dynamic story */}
        <div key={branch} className="grid grid-cols-1 lg:grid-cols-12 gap-12 fade-in-story">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Our Story · {story.label}</p>
            <h2 className="font-display text-4xl lg:text-5xl text-white leading-[1]">
              {story.heading}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-base lg:text-lg text-white/70 leading-relaxed">
            {story.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* INDIA branch sections — Mission/Vision, Values, Timeline */}
      {branch === "india" && (
        <>
          {/* Mission/Vision */}
          <section className="py-24 ember-bg border-y border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
          <div className="bg-[#0a0a0a] p-10 lg:p-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ff4500] mb-4">◆ Mission</p>
            <h3 className="font-display text-3xl lg:text-4xl text-white leading-tight">
              To make world-class dance training accessible to anyone, anywhere — without compromise.
            </h3>
          </div>
          <div className="bg-[#0a0a0a] p-10 lg:p-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Vision</p>
            <h3 className="font-display text-3xl lg:text-4xl text-white leading-tight">
              A global community of dancers connected by craft, courage, and the Phoenix spirit.
            </h3>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow="What we live by" title="Five values." accent="One mindset." testid="values-heading" />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-white/5">
          {VALUES.map((v, i) => (
            <div key={i} data-testid={`value-${i}`} className="bg-[#0a0a0a] p-8 hover:bg-[#0f0f0f] transition-colors">
              <p className="font-display text-5xl phoenix-text">0{i + 1}</p>
              <h4 className="font-display text-2xl text-white mt-4">{v.title}</h4>
              <p className="text-sm text-white/60 mt-3 leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

          {/* Milestones */}
          <section className="py-24 lg:py-32 bg-[#070707] border-y border-white/10">
            <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
              <SectionHeading eyebrow="Timeline" title="A decade of" accent="movement." testid="milestones-heading" />
              <div className="relative pl-10 lg:pl-16">
                <div className="absolute top-0 bottom-0 left-3 lg:left-6 w-px bg-gradient-to-b from-[#ff4500] via-[#ffb700] to-transparent" />
                {MILESTONES.map((m, i) => (
                  <div key={i} data-testid={`milestone-${i}`} className="relative pb-12 last:pb-0">
                    <div className="absolute -left-[31px] lg:-left-[37px] top-1 w-3 h-3 bg-[#ff4500] phoenix-glow" />
                    <p className="font-display text-3xl lg:text-4xl phoenix-text">{m.year}</p>
                    <h4 className="font-display text-xl lg:text-2xl text-white mt-2">{m.title}</h4>
                    <p className="text-sm lg:text-base text-white/60 mt-2 leading-relaxed max-w-2xl">{m.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* USA branch sections */}
      {branch === "usa" && <USAExperience />}

      {/* CANADA branch sections */}
      {branch === "canada" && <CanadaExperience />}

      {/* International presence */}
      <section className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow="Worldwide" title="Three countries." accent="One Phoenix." testid="presence-heading" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {BRANCHES.map((b) => (
            <div key={b.id} className="bg-[#0a0a0a] border border-white/10 p-8 card-hover">
              <div className="text-5xl mb-4">{b.flag}</div>
              <h3 className="font-display text-3xl text-white">{b.name}</h3>
              <p className="text-xs uppercase tracking-[0.2em] text-[#ffb700] mt-2">{b.city}</p>
              <div className="mt-4 space-y-3">
                {b.locations.map((loc) => (
                  <div key={loc.id} className="text-sm text-white/60">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">{loc.name}</p>
                    <p>{loc.address}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-white/10 ember-bg">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h3 className="font-display text-3xl lg:text-4xl text-white leading-tight">
            Want to be part of the Phoenix family?
          </h3>
          <Link to="/book-trial">
            <button data-testid="about-cta-btn" className="btn-phoenix bg-[#ff4500] text-white px-8 h-14 uppercase tracking-wider text-sm font-semibold inline-flex items-center gap-2">
              Join PDA <ChevronRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}


/* =====================================================
   USA EXPERIENCE — visual layout, no essay paragraphs
   ===================================================== */

const USA_STUDIO_STYLES = [
  "Hip Hop", "Bollywood", "Indian Cinematic", "Indian Contemporary",
  "Bhangra", "Semi-Classical Fusion", "Dance Fitness & Bollywood Zumba", "Toddler Programs",
];
const USA_AGE_GROUPS = ["Toddlers", "Kids", "Tweens", "Teens", "Adults"];
const USA_ONLINE_PROGRAMS = [
  "Hip Hop Foundations", "Choreography Classes", "Performance Teams",
  "Summer Camps", "Technique Training", "Competition Prep",
];
const USA_ONLINE_PERKS = [
  "Personalized corrections from instructors",
  "Detailed choreography breakdowns",
  "Performance & stage opportunities",
  "1-on-1 mentorship sessions",
];
const USA_PARTNERS = [
  "FetNA — San Antonio", "FetNA — Sacramento", "Sacramento Tamil Mandrum",
  "Kannada Sangha of Sacramento", "Natomas Group", "Natomas Tamil Kudumbam",
  "Sacramento Telangana Association", "Indian Association of Sacramento",
  "Sanskrity", "Telugu Association of Greater Sacramento",
];
const USA_PERFORMANCE_QUALITIES = [
  { l: "Professional choreography", I: Music },
  { l: "Strong storytelling", I: BookOpen },
  { l: "Powerful stage presence", I: Star },
  { l: "Indo-Western fusion", I: Sparkles },
  { l: "High-energy synchronization", I: Zap },
];
const USA_PRODUCTIONS = [
  { l: "Cultural Festivals", I: Sparkles },
  { l: "Community Events", I: Users },
  { l: "School Programs", I: GraduationCap },
  { l: "Annual Shows", I: Calendar },
  { l: "Talent Competitions", I: Trophy },
  { l: "Corporate & Private", I: Briefcase },
];
const USA_BLEND = [
  "Indian culture", "Contemporary movement", "Cinematic storytelling",
  "Western dance styles", "Folk traditions", "Creative stage concepts",
];
const USA_PRIVATE_EVENTS = [
  { l: "Weddings", I: Heart },
  { l: "Sangeet", I: Music },
  { l: "Birthdays", I: PartyPopper },
  { l: "Corporate Events", I: Briefcase },
  { l: "Talent Shows", I: Star },
  { l: "Cultural Programs", I: Sparkles },
  { l: "Solos & Duets", I: Trophy },
];
const USA_WHY = [
  { t: "Professional Indo-Western Training", d: "Industry-grade choreography blended across cultures." },
  { t: "Family-Friendly Environment", d: "A safe, supportive space for every age group." },
  { t: "Performance Opportunities", d: "Real stage time at festivals, competitions, and shows." },
  { t: "Strong Technical Foundation", d: "Structured technique, drills, and progression." },
  { t: "Experienced Choreographers", d: "Trained under PDA founders and visiting masters." },
  { t: "Fun, Positive & Inclusive", d: "Beginners welcome — everyone learns at their pace." },
  { t: "Classes for All Ages & Skills", d: "Toddlers to adults, beginner to advanced." },
];

function TrophyCard({ title, place, piece, note, featured }) {
  return (
    <div className={`relative border p-7 transition-colors card-hover ${featured ? "border-[#ff4500]/50 bg-gradient-to-br from-[#ff4500]/10 via-[#0a0a0a] to-[#0a0a0a]" : "border-white/10 bg-[#0a0a0a]"}`}>
      {featured && (
        <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.2em] text-[#ffb700] border border-[#ffb700]/40 px-2 py-1">
          ◆ Title Win
        </span>
      )}
      <Trophy className={`w-7 h-7 ${featured ? "text-[#ffb700]" : "text-[#ff4500]"} mb-4`} strokeWidth={1.5} />
      <p className="text-[11px] uppercase tracking-[0.25em] text-white/45 mb-1">{title}</p>
      <p className="font-display text-2xl text-white leading-tight">{place}</p>
      {piece && <p className="text-sm text-white/70 mt-3 italic">{piece}</p>}
      {note && <p className="text-xs text-white/50 mt-3">{note}</p>}
    </div>
  );
}

function USAExperience() {
  return (
    <>
      {/* USA Mission / Vision */}
      <section className="py-24 ember-bg border-y border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
          <div className="bg-[#0a0a0a] p-10 lg:p-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ff4500] mb-4">◆ Mission</p>
            <h3 className="font-display text-3xl lg:text-4xl text-white leading-tight">
              Professional Indo-Western training built on discipline, fitness, and creative storytelling.
            </h3>
          </div>
          <div className="bg-[#0a0a0a] p-10 lg:p-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Vision</p>
            <h3 className="font-display text-3xl lg:text-4xl text-white leading-tight">
              Inspire a global Indo-Western dance community through unforgettable stage performances.
            </h3>
          </div>
        </div>
      </section>

      {/* What We Offer — Studio + Online */}
      <section className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow="What we offer" title="Studio +" accent="Online." testid="usa-offer-heading" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Studio */}
          <div className="border border-white/10 bg-[#0a0a0a] p-8 lg:p-10 card-hover">
            <div className="flex items-center gap-3 mb-3">
              <Building2 className="w-5 h-5 text-[#ff4500]" />
              <p className="text-xs uppercase tracking-[0.25em] text-[#ffb700]">In-person</p>
            </div>
            <h3 className="font-display text-3xl text-white">Studio Classes</h3>
            <p className="text-sm text-white/55 mt-2 inline-flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-[#ff4500]" /> PDA Studio — NorthLake Sacramento
            </p>
            <p className="text-base text-white/70 mt-5 leading-relaxed">
              Structured dance training in a fun, positive environment.
            </p>
            <div className="mt-7">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 mb-3">Styles offered</p>
              <div className="flex flex-wrap gap-2">
                {USA_STUDIO_STYLES.map((s) => (
                  <span key={s} className="text-xs px-3 h-8 inline-flex items-center border border-white/15 text-white/75">
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <div className="mt-6">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 mb-3">Age groups</p>
              <div className="flex flex-wrap gap-2">
                {USA_AGE_GROUPS.map((a) => (
                  <span key={a} className="text-xs px-3 h-8 inline-flex items-center bg-[#ff4500]/10 border border-[#ff4500]/40 text-[#ff4500] font-medium">
                    {a}
                  </span>
                ))}
              </div>
            </div>

            {/* Go to Classes */}
            <Link
              to="/classes"
              data-testid="usa-studio-classes-cta"
              className="mt-8 inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 h-12 bg-[#ff4500] text-white text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#ffb700] hover:text-black transition-colors btn-phoenix"
            >
              Go to Classes
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Online */}
          <div className="border border-white/10 bg-[#0a0a0a] p-8 lg:p-10 card-hover">
            <div className="flex items-center gap-3 mb-3">
              <Globe className="w-5 h-5 text-[#ffb700]" />
              <p className="text-xs uppercase tracking-[0.25em] text-[#ffb700]">Worldwide</p>
            </div>
            <h3 className="font-display text-3xl text-white">Online Programs</h3>
            <p className="text-sm text-white/55 mt-2">
              Live Zoom sessions led by PDA India &amp; USA instructors.
            </p>
            <div className="mt-7">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 mb-3">Programs</p>
              <div className="flex flex-wrap gap-2">
                {USA_ONLINE_PROGRAMS.map((p) => (
                  <span key={p} className="text-xs px-3 h-8 inline-flex items-center border border-white/15 text-white/75">
                    {p}
                  </span>
                ))}
              </div>
            </div>
            <div className="mt-7 pt-6 border-t border-white/10 space-y-2">
              {USA_ONLINE_PERKS.map((p, i) => (
                <p key={i} className="text-sm text-white/70 flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#25D366] mt-0.5 shrink-0" /> {p}
                </p>
              ))}
            </div>

            {/* Go to Classes */}
            <Link
              to="/classes"
              data-testid="usa-online-classes-cta"
              className="mt-8 inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 h-12 bg-[#ff4500] text-white text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#ffb700] hover:text-black transition-colors btn-phoenix"
            >
              Go to Classes
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Competitions & Achievements */}
      <section className="py-24 lg:py-32 bg-[#070707] border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow="Recognition" title="Awards &" accent="achievements." testid="usa-awards-heading" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            <TrophyCard
              featured
              title="USA Got Talent — Bay Area"
              place="Title Winner · 1st Place"
              piece='"Power & Grace"'
              note="Judged by Kala Master & Actress Meena"
            />
            <TrophyCard
              title="Sargolsav Dance Competition"
              place="Teens · 1st Place"
              piece="PDA USA Senior Team"
            />
            <TrophyCard
              title="Sargolsav Dance Competition"
              place="Juniors · 3rd Place"
              piece="PDA USA Junior Team"
            />
          </div>

          {/* Community partners */}
          <div className="border border-white/10 bg-[#0a0a0a] p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-[#ffb700] mb-4">◆ Cultural &amp; community partnerships</p>
            <p className="text-sm text-white/60 mb-5 max-w-3xl">
              Multiple stage recognitions across California and the USA in collaboration with leading cultural associations:
            </p>
            <div className="flex flex-wrap gap-2">
              {USA_PARTNERS.map((o) => (
                <span key={o} className="text-xs px-3 h-8 inline-flex items-center border border-white/15 text-white/75">
                  {o}
                </span>
              ))}
            </div>
          </div>

          {/* Performance qualities */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-px bg-white/5">
            {USA_PERFORMANCE_QUALITIES.map((q, i) => (
              <div key={i} className="bg-[#0a0a0a] p-6">
                <q.I className="w-5 h-5 text-[#ff4500] mb-3" />
                <p className="text-sm text-white font-medium leading-snug">{q.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Performances & Productions */}
      <section className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow="On stage" title="Performances &" accent="productions." testid="usa-perf-heading" />
        <p className="text-white/65 max-w-3xl text-base lg:text-lg leading-relaxed mb-10 -mt-2">
          PDA USA regularly presents large-scale performances and collaborates with community organizations for cultural showcases.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
          {USA_PRODUCTIONS.map((x, i) => (
            <div key={i} className="border border-white/10 bg-[#0a0a0a] p-5 card-hover">
              <x.I className="w-5 h-5 text-[#ff4500] mb-3" />
              <p className="text-sm text-white font-medium">{x.l}</p>
            </div>
          ))}
        </div>
        <div className="border border-white/10 bg-[#0a0a0a] p-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#ffb700] mb-4">◆ Themed productions blend</p>
          <div className="flex flex-wrap gap-2">
            {USA_BLEND.map((t) => (
              <span key={t} className="text-xs px-3 h-8 inline-flex items-center bg-[#ffb700]/10 border border-[#ffb700]/40 text-[#ffb700] font-medium">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Private Lessons & Event Choreography */}
      <section className="py-24 lg:py-32 bg-[#070707] border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Custom choreography</p>
            <h2 className="font-display text-4xl lg:text-5xl text-white leading-[1]">
              Private lessons &amp;<br />
              <span className="phoenix-text">event choreography.</span>
            </h2>
            <p className="mt-6 text-base text-white/65 leading-relaxed max-w-xl">
              Personalized 1-on-1 coaching and custom choreography for every special moment — online or in-person.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 px-4 h-10 border border-[#ff4500]/40 text-[#ff4500] text-xs uppercase tracking-wider font-semibold">
                <Globe className="w-4 h-4" /> Online &amp; In-person
              </div>
              <a
                href={`https://wa.me/19048078000?text=${encodeURIComponent(
                  "Hi PDA USA! I'd like to enquire about Private Lessons & Event Choreography. Please share details on availability, packages, and pricing."
                )}`}
                target="_blank"
                rel="noreferrer"
                data-testid="usa-private-enquire"
                className="inline-flex items-center gap-2 px-5 h-10 bg-[#25D366] text-white text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#1ebd5b] transition-colors btn-phoenix"
              >
                <MessageCircle className="w-4 h-4" />
                Enquire on WhatsApp
                <ChevronRight className="w-4 h-4" />
              </a>
              <a
                href={`mailto:phoenixdanceacademyusa@gmail.com?subject=${encodeURIComponent(
                  "Private Lessons & Event Choreography — Enquiry"
                )}&body=${encodeURIComponent(
                  "Hi PDA USA,\n\nI'd like to enquire about Private Lessons & Event Choreography. Could you share details on availability, packages, and pricing?\n\nThanks!"
                )}`}
                data-testid="usa-private-enquire-email"
                className="inline-flex items-center gap-2 px-5 h-10 bg-[#ff4500] text-white text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#ffb700] hover:text-black transition-colors btn-phoenix"
              >
                <Mail className="w-4 h-4" />
                Enquire via Email
              </a>
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {USA_PRIVATE_EVENTS.map((x, i) => (
              <div key={i} className="border border-white/10 bg-[#0a0a0a] p-5 card-hover">
                <x.I className="w-5 h-5 text-[#ffb700] mb-3" />
                <p className="text-sm text-white font-medium">{x.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose PDA USA — replaces "What We Live By" */}
      <section className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow="Why choose PDA USA" title="Seven reasons" accent="to dance with us." testid="usa-why-heading" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
          {USA_WHY.map((v, i) => (
            <div key={i} className="bg-[#0a0a0a] p-8 hover:bg-[#0f0f0f] transition-colors">
              <Sparkles className="w-5 h-5 text-[#ffb700] mb-4" />
              <h4 className="font-display text-xl text-white">{v.t}</h4>
              <p className="text-sm text-white/60 mt-3 leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-12 text-center text-base lg:text-lg text-white/65 max-w-3xl mx-auto leading-relaxed">
          At PDA USA, every student is encouraged to grow with{" "}
          <span className="text-[#ff4500] font-medium">confidence</span>,{" "}
          <span className="text-[#ffb700] font-medium">creativity</span>, and{" "}
          <span className="text-[#ff4500] font-medium">passion</span> — building lifelong memories through dance.
        </p>
      </section>

      {/* Join the PDA Family — Studio location + CTA */}
      <section className="py-24 lg:py-32 ember-bg border-y border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Join the PDA family</p>
          <h2 className="font-display text-4xl lg:text-6xl text-white leading-[0.95] max-w-3xl">
            Whether it's recreation, competition, fitness, or choreography —
            <span className="phoenix-text"> we have a place for you.</span>
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
            <div className="bg-[#0a0a0a] p-8">
              <MapPin className="w-5 h-5 text-[#ff4500] mb-3" />
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Studio</p>
              <p className="text-white font-medium">NorthLake</p>
              <p className="text-sm text-white/65">Sacramento, California</p>
            </div>
            <div className="bg-[#0a0a0a] p-8">
              <Globe className="w-5 h-5 text-[#ffb700] mb-3" />
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Online</p>
              <p className="text-white font-medium">Classes Worldwide</p>
              <p className="text-sm text-white/65">Live Zoom across time zones</p>
            </div>
          </div>
          <p className="mt-12 font-display text-2xl lg:text-3xl text-white tracking-wide flex flex-wrap gap-x-3 gap-y-1">
            <span className="text-[#ff4500]">Dance.</span>
            <span className="text-[#ffb700]">Perform.</span>
            <span className="text-[#ff4500]">Inspire.</span>
            <span className="phoenix-text">Shine.</span>
          </p>
        </div>
      </section>
    </>
  );
}


/* =====================================================
   CANADA EXPERIENCE — Kitchener / Sandhya Vijayakumar
   ===================================================== */

const CAN_STUDIO_STYLES = ["Indian Cinematic", "Indian Folk", "Contemporary", "Hip-Hop"];
const CAN_AGE_GROUPS = ["Kids", "Teens", "Adults (Women's)", "Juniors"];

const CAN_PERFORMANCES = [
  "TCA Thai Pongal Thiruvizha",
  "Waterloo Murugan Temple Thai Poosam",
  "Sathya Narayana Temple Kanda Shasti Kondattam",
  "KWC Bengali Event",
  "Gujarathi Diwali Event",
  "Punjabi Mela",
  "Yukthi Event",
  "Grand River Kannada Diwali Event",
  "Grand River Kannada Ugadhi Event",
  "The Grand Festival",
  "TCA Chithirai Thiruvizha",
  "Colors of India",
  "Carrasauga",
  "India's The Republic Day Event",
  "Kalai Vizha",
  "Indian Vibes",
];

const CAN_COMPETITIONS = [
  {
    title: "Canada's Thumakda",
    place: "1st Prize",
    note: "Won by PDA Canada Students",
    featured: true,
  },
  {
    title: "Gala Competition — Dual Round",
    place: "1st Prize",
    note: "Won by PDA Canada Students",
  },
  {
    title: "Gala Competition — Age 5–8 Zone",
    place: "2nd Prize",
    note: "Won by PDA Canada Students",
  },
];

const CAN_WHY = [
  { t: "Inclusive for Every Age", d: "Programs designed for kids, teens, women, and adults — beginners welcome." },
  { t: "Indo-Western Specialisation", d: "Cinematic, Folk, Contemporary, and Hip-Hop — taught with cultural depth." },
  { t: "Mentored by PDA Founder", d: "Curriculum aligned with PDA India, regularly reviewed by Manikandan Gopinath." },
  { t: "Performance Opportunities", d: "Real stage time across 16+ Ontario cultural events every year." },
  { t: "Competition-Ready Coaching", d: "Multiple Gala Competition wins and Canada's Thumakda 1st Prize." },
  { t: "Wedding & Event Choreography", d: "Custom routines for weddings, baby showers, and live music shows." },
  { t: "Growing Student Family", d: "From 3 kids to 25+ active students within one year — and counting." },
];

function CanadaExperience() {
  return (
    <>
      {/* Vision + Purpose */}
      <section className="py-24 ember-bg border-y border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
          <div className="bg-[#0a0a0a] p-10 lg:p-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ff4500] mb-4">◆ Vision · PDA Canada</p>
            <h3 className="font-display text-3xl lg:text-4xl text-white leading-tight">
              Inspire confidence, creativity, and cultural connection — to grow across multiple locations and teach 100+ students by 2027 in our very own studio.
            </h3>
          </div>
          <div className="bg-[#0a0a0a] p-10 lg:p-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Purpose · PDA Canada</p>
            <h3 className="font-display text-3xl lg:text-4xl text-white leading-tight">
              Empowering kids, teens, men, and women of all ages to overcome hesitation and rediscover themselves through dance.
            </h3>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow="What we offer" title="Studio classes in" accent="Kitchener." testid="can-offer-heading" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-white/10 bg-[#0a0a0a] p-8 lg:p-10 card-hover">
            <div className="flex items-center gap-3 mb-3">
              <Building2 className="w-5 h-5 text-[#ff4500]" />
              <p className="text-xs uppercase tracking-[0.25em] text-[#ffb700]">In-person</p>
            </div>
            <h3 className="font-display text-3xl text-white">Studio Classes</h3>
            <p className="text-sm text-white/55 mt-2 inline-flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-[#ff4500]" /> 37 Eliza Ave, Kitchener, N2E 0G2, Ontario
            </p>
            <p className="text-base text-white/70 mt-5 leading-relaxed">
              Regular classes, wedding choreography, baby showers, and competition coaching — all in a positive, energetic environment.
            </p>
            <div className="mt-7">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 mb-3">Styles taught</p>
              <div className="flex flex-wrap gap-2">
                {CAN_STUDIO_STYLES.map((s) => (
                  <span key={s} className="text-xs px-3 h-8 inline-flex items-center border border-white/15 text-white/75">{s}</span>
                ))}
              </div>
            </div>
            <div className="mt-6">
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 mb-3">Age groups</p>
              <div className="flex flex-wrap gap-2">
                {CAN_AGE_GROUPS.map((a) => (
                  <span key={a} className="text-xs px-3 h-8 inline-flex items-center bg-[#ff4500]/10 border border-[#ff4500]/40 text-[#ff4500] font-medium">{a}</span>
                ))}
              </div>
            </div>
            <Link
              to="/classes"
              data-testid="can-studio-classes-cta"
              className="mt-8 inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 h-12 bg-[#ff4500] text-white text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#ffb700] hover:text-black transition-colors btn-phoenix"
            >
              Go to Classes
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="border border-white/10 bg-[#0a0a0a] p-8 lg:p-10 card-hover">
            <div className="flex items-center gap-3 mb-3">
              <Globe className="w-5 h-5 text-[#ffb700]" />
              <p className="text-xs uppercase tracking-[0.25em] text-[#ffb700]">Online · From Canada</p>
            </div>
            <h3 className="font-display text-3xl text-white">Online Programs</h3>
            <p className="text-sm text-white/55 mt-2">Live online classes for Juniors and Professional teens & adults.</p>
            <div className="mt-7 space-y-2">
              {[
                "Juniors (8–12) — Intermediate level",
                "Teens & Adults (13+) — Professional level (3+ years experience)",
                "Personalised corrections from Sandhya & Manikandan",
                "Performance & competition mentorship",
              ].map((p, i) => (
                <p key={i} className="text-sm text-white/70 flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#25D366] mt-0.5 shrink-0" /> {p}
                </p>
              ))}
            </div>
            <Link
              to="/classes"
              data-testid="can-online-classes-cta"
              className="mt-8 inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 h-12 bg-[#ff4500] text-white text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#ffb700] hover:text-black transition-colors btn-phoenix"
            >
              Go to Classes
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Competitions */}
      <section className="py-24 lg:py-32 bg-[#070707] border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow="Competitions" title="Award-winning" accent="student wins." testid="can-comp-heading" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {CAN_COMPETITIONS.map((c, i) => (
              <TrophyCard key={i} featured={c.featured} title={c.title} place={c.place} note={c.note} />
            ))}
          </div>

          {/* Performances */}
          <SectionHeading eyebrow="On stage" title="16+ performances" accent="across Ontario." testid="can-perf-heading" />
          <p className="text-white/65 max-w-3xl text-base lg:text-lg leading-relaxed mb-8 -mt-2">
            From temple celebrations to multicultural festivals, our students have lit up stages across Ontario's South-Asian community.
          </p>
          <div className="border border-white/10 bg-[#0a0a0a] p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {CAN_PERFORMANCES.map((p, i) => (
                <div key={i} className="flex items-start gap-3 text-sm text-white/75">
                  <span className="text-[#ffb700] mt-0.5 shrink-0">◆</span>
                  <span>{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose PDA Canada */}
      <section className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow="Why choose PDA Canada" title="Seven reasons" accent="to dance with us." testid="can-why-heading" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
          {CAN_WHY.map((v, i) => (
            <div key={i} className="bg-[#0a0a0a] p-8 hover:bg-[#0f0f0f] transition-colors">
              <Sparkles className="w-5 h-5 text-[#ffb700] mb-4" />
              <h4 className="font-display text-xl text-white">{v.t}</h4>
              <p className="text-sm text-white/60 mt-3 leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-24 lg:py-32 ember-bg border-y border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Join the PDA family</p>
          <h2 className="font-display text-4xl lg:text-6xl text-white leading-[0.95] max-w-3xl">
            Hesitation ends here.
            <span className="phoenix-text"> Your dance story begins.</span>
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
            <div className="bg-[#0a0a0a] p-8">
              <MapPin className="w-5 h-5 text-[#ff4500] mb-3" />
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Studio</p>
              <p className="text-white font-medium">37 Eliza Ave</p>
              <p className="text-sm text-white/65">Kitchener, N2E 0G2, Ontario</p>
            </div>
            <a
              href="https://wa.me/16478982549?text=Hi%20PDA%20Canada%21%20I%27d%20like%20to%20enquire%20about%20classes."
              target="_blank"
              rel="noreferrer"
              className="bg-[#0a0a0a] p-8 hover:bg-[#0f0f0f] transition-colors block group"
              data-testid="can-whatsapp"
            >
              <Globe className="w-5 h-5 text-[#25D366] mb-3" />
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">WhatsApp</p>
              <p className="text-white font-medium group-hover:text-[#25D366] transition-colors">+1 647 898 2549</p>
              <p className="text-sm text-white/65">Tap to message Sandhya</p>
            </a>
          </div>
          <p className="mt-12 font-display text-2xl lg:text-3xl text-white tracking-wide flex flex-wrap gap-x-3 gap-y-1">
            <span className="text-[#ff4500]">Dance.</span>
            <span className="text-[#ffb700]">Perform.</span>
            <span className="text-[#ff4500]">Inspire.</span>
            <span className="phoenix-text">Shine.</span>
          </p>
        </div>
      </section>
    </>
  );
}
